/**
 * Product buy box — title, brand link, applicable special, attribute chips,
 * price + after-tax line, variant picker, qty stepper, Add-to-Cart CTA,
 * description, store availability footer.
 */
import { Link } from "react-router-dom";
import { Star, Plus, ShoppingBag, Tag } from "lucide-react";
import { withTax, fmtRound } from "../../lib/tax";
import { brandSlug } from "../../lib/brand";

export default function ProductBuyBox({
    product,
    storeSlug,
    store,
    variants,
    variantIdx,
    setVariantIdx,
    v0,
    inStock,
    isOnSale,
    applicableSpecial,
    qty,
    setQty,
    onAdd,
    cartLoading,
    setCartOpen,
}) {
    return (
        <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                {product.brand?.name ? (
                    <Link
                        to={`/menu/${storeSlug}/brand/${brandSlug(product.brand.name)}`}
                        data-testid="product-detail-brand-link"
                        className="hover:text-zen-ink underline-offset-2 hover:underline"
                    >
                        {product.brand.name}
                    </Link>
                ) : (
                    "ZenZest"
                )}{" "}
                · {product.category}
                {product.subcategory ? ` · ${product.subcategory}` : ""}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-tight mb-6">
                {product.name}
            </h1>

            {applicableSpecial && (
                <Link
                    to={`/menu/${storeSlug}/special/${applicableSpecial.id}`}
                    data-testid="product-detail-promo"
                    className="inline-flex items-center gap-2 mb-6 px-3 py-2 bg-red-600 text-white text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-red-700 transition-colors max-w-full"
                >
                    <Tag size={12} className="shrink-0" />
                    <span className="truncate">{applicableSpecial.name}</span>
                </Link>
            )}

            <div className="flex items-center gap-3 mb-6 flex-wrap">
                {product.strainType && product.strainType !== "NOT_APPLICABLE" && (
                    <span className="px-3 py-1 border border-zen-ink/15 text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft">
                        {product.strainType}
                    </span>
                )}
                {product.potencyThc?.formatted && (
                    <span className="px-3 py-1 border border-zen-ink/15 text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft">
                        THC {product.potencyThc.formatted}
                    </span>
                )}
                {product.potencyCbd?.formatted &&
                    product.potencyCbd.range?.[0] > 0 && (
                        <span className="px-3 py-1 border border-zen-ink/15 text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft">
                            CBD {product.potencyCbd.formatted}
                        </span>
                    )}
                {product.staffPick && (
                    <span className="px-3 py-1 bg-zen-olive/10 border border-zen-olive/40 text-[10px] uppercase tracking-[0.22em] text-zen-olive inline-flex items-center gap-1">
                        <Star size={10} fill="#6B7C4F" /> Staff pick
                    </span>
                )}
            </div>

            {v0 && (
                <div className="mb-8 border-y border-zen-ink/10 py-6">
                    <div className="flex items-baseline gap-3">
                        <span className="font-serif text-4xl font-light text-zen-ink">
                            ${(v0.specialPriceRec || v0.priceRec).toFixed(2)}
                        </span>
                        {isOnSale && (
                            <span className="text-lg text-zen-muted line-through">
                                ${v0.priceRec.toFixed(2)}
                            </span>
                        )}
                        {v0.option && (
                            <span className="text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft">
                                · {v0.option}
                            </span>
                        )}
                    </div>
                    <p
                        data-testid="product-detail-aftertax"
                        className="text-xs text-zen-ink-soft mt-1.5"
                    >
                        {fmtRound(withTax(v0.specialPriceRec || v0.priceRec, v0))}
                        <span className="text-zen-muted ml-1">
                            after tax · NY tax computed on pre-discount price
                        </span>
                    </p>
                    <p
                        className={`text-[11px] uppercase tracking-[0.22em] mt-2 ${
                            inStock ? "text-zen-olive" : "text-zen-muted"
                        }`}
                    >
                        {inStock
                            ? `In stock · ${v0.quantity} available`
                            : "Out of stock"}
                    </p>
                </div>
            )}

            {variants.length > 1 && (
                <div className="mb-6">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-zen-ink-soft mb-2">
                        Size
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {variants.map((v, i) => (
                            <button
                                key={i}
                                onClick={() => setVariantIdx(i)}
                                data-testid={`product-variant-${i}`}
                                className={`px-4 py-2 border text-sm transition-colors ${
                                    i === variantIdx
                                        ? "border-zen-olive bg-zen-olive/10 text-zen-ink"
                                        : "border-zen-ink/15 text-zen-ink-soft hover:border-zen-ink/40"
                                }`}
                            >
                                {v.option}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <div className="inline-flex items-center border border-zen-ink/15 self-start">
                    <button
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        data-testid="product-qty-dec"
                        className="px-4 py-3 text-zen-ink hover:bg-zen-ink/5"
                        aria-label="Decrease quantity"
                    >
                        −
                    </button>
                    <span className="px-5 font-mono text-zen-ink min-w-[40px] text-center">
                        {qty}
                    </span>
                    <button
                        onClick={() => setQty((q) => q + 1)}
                        data-testid="product-qty-inc"
                        className="px-4 py-3 text-zen-ink hover:bg-zen-ink/5"
                        aria-label="Increase quantity"
                    >
                        +
                    </button>
                </div>
                <button
                    onClick={onAdd}
                    disabled={!inStock || cartLoading}
                    data-testid="product-add-cta"
                    className="inline-flex items-center justify-center gap-2 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg flex-1 sm:flex-initial px-10 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Plus size={14} strokeWidth={2.5} />
                    {cartLoading ? "Adding…" : inStock ? "Add to cart" : "Out of stock"}
                </button>
            </div>
            <button
                onClick={() => setCartOpen(true)}
                data-testid="product-view-cart"
                className="text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink mb-6 inline-flex items-center gap-2"
            >
                <ShoppingBag size={12} /> View cart
            </button>

            {product.description && (
                <div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        About this product
                    </p>
                    <div className="text-zen-ink-soft leading-relaxed text-sm whitespace-pre-line">
                        {product.description}
                    </div>
                </div>
            )}

            {store && (
                <div className="mt-12 border-t border-zen-ink/10 pt-8">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                        Available at
                    </p>
                    <Link
                        to={`/locations/${storeSlug}`}
                        className="group block"
                    >
                        <p className="font-serif text-xl text-zen-ink font-light mb-1 group-hover:text-zen-olive transition-colors">
                            {store.name}
                        </p>
                        <p className="text-sm text-zen-ink-soft">
                            {store.street}, {store.city}, {store.state} {store.zip}
                        </p>
                    </Link>
                </div>
            )}
        </div>
    );
}
